import React, { cloneElement, useEffect, useState } from 'react'
import { Table } from 'semantic-ui-react'
import { PaginationComponent } from './Pagination'
import { DesktopMode, MobileMode } from 'layout'
import styled from 'styled-components'

const DesktopTable = ({
    columns = [],
    data = [],
    draggingIndex,
    onDragStart,
    onDrop,
    onDragEnd,
    onDragOver,
    draggable,
    onRowClick,
}) => (
    <Table celled>
        <Table.Header>
            <Table.Row>
                {columns?.map((cell, index) => (
                    <Table.HeaderCell
                        key={index}
                        textAlign={cell?.textAlign || 'center'}
                        style={{ width: cell?.width || 'auto' }} // Apply column width
                    >
                        {cell?.title || ''}
                    </Table.HeaderCell>
                ))}
            </Table.Row>
        </Table.Header>
        <Table.Body>
            {data?.map((item, rowIndex) => (
                <Table.Row
                    key={item.Id || rowIndex}
                    draggable={onRowClick ? false : draggable}
                    className={
                        onRowClick
                            ? 'clickable-row'
                            : `draggable ${
                                  draggingIndex === rowIndex ? 'dragging' : ''
                              }`
                    }
                    onClick={
                        onRowClick ? () => onRowClick(item) : undefined
                    }
                    onDragStart={
                        onRowClick ? undefined : e => onDragStart(e, rowIndex)
                    }
                    onDragOver={onRowClick ? undefined : e => onDragOver(e)}
                    onDragEnd={onRowClick ? undefined : onDragEnd}
                    onTouchStart={
                        onRowClick
                            ? undefined
                            : e => onDragStart(e, rowIndex)
                    }
                    onTouchMove={onRowClick ? undefined : onDragOver}
                    onTouchEnd={onRowClick ? undefined : onDragEnd}
                    onTouchCancel={onRowClick ? undefined : onDragEnd}
                    onDrop={
                        onRowClick
                            ? undefined
                            : e => {
                                  onDrop(e, rowIndex)
                              }
                    }
                >
                    {columns?.map((column, colIndex) => (
                        <Table.Cell
                            key={colIndex}
                            textAlign={column?.textAlign || 'center'}
                            style={{ width: column?.width || 'auto' }} // Apply column width
                        >
                            {item?.[column?.key] ||
                                (column?.render && column?.render(item))}
                        </Table.Cell>
                    ))}
                </Table.Row>
            ))}
        </Table.Body>
    </Table>
)

const TableConatiner = styled.div`
    .draggable {
        cursor: move;
        cursor: grab;
        cursor: -moz-grab;
        cursor: -webkit-grab;
    }
    .dragging {
        background-color: #f8f8f9 !important;
    }
    .clickable-row {
        cursor: pointer;
    }
    .clickable-row:hover {
        background: #f8fafc !important;
    }
`

export const AppTable = ({
    columns,
    data,
    filter,
    pageChanged,
    totalCount,
    mobileCard,
    onDragCompleted,
    draggable = false,
    hidePagination = false,
    emptyMessage, // <-- Add this prop
    onRowClick,
}) => {
    const [list, setList] = useState(data)
    const [draggingIndex, setDraggingIndex] = useState(null)

    useEffect(() => {
        setList(data)
    }, [data])

    const onDragStart = (ev, i) => {
        if (ev.dataTransfer) {
            ev.dataTransfer.setData('index', i)
            setDraggingIndex(i)
        }
    }

    const onDragEnd = () => {
        setDraggingIndex(null)
    }

    const onDragOver = ev => {
        ev.preventDefault()
    }

    const onDrop = (ev, i) => {
        const draggedIndex = parseInt(ev.dataTransfer.getData('index'), 10)
        if (draggedIndex !== i) {
            const updatedList = Array.from(list)
            const [draggedItem] = updatedList.splice(draggedIndex, 1)
            updatedList.splice(i, 0, draggedItem)
            setList(
                updatedList.map((item, index) => ({ ...item, Order: index })),
            )
            if (onDragCompleted) {
                onDragCompleted(updatedList)
            }
        }
        setDraggingIndex(null)
    }
    return (
        <TableConatiner>
            <DesktopMode>
                {data.length === 0 ? (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '180px',
                        background: '#fafbfc',
                        borderRadius: '8px',
                        border: '1px solid #eee',
                        margin: '20px 0',
                        fontSize: '1.1rem',
                        color: '#888',
                        fontWeight: 500,
                    }}>
                        <span>
                            <i className="inbox icon" style={{ fontSize: 32, marginRight: 12, color: '#bbb' }} />
                            {emptyMessage || 'No Data Found'}
                        </span>
                    </div>
                ) : (
                    <DesktopTable
                        columns={columns}
                        data={data}
                        onDragStart={onDragStart}
                        onDragOver={onDragOver}
                        draggable={draggable}
                        onDragEnd={onDragEnd}
                        onDrop={onDrop}
                        draggingIndex={draggingIndex}
                        onRowClick={onRowClick}
                    />
                )}
            </DesktopMode>
            <MobileMode>
                {data.length === 0 && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '120px',
                        background: '#fafbfc',
                        borderRadius: '8px',
                        border: '1px solid #eee',
                        margin: '16px 0',
                        fontSize: '1rem',
                        color: '#888',
                        fontWeight: 500,
                    }}>
                        <span>
                            <i className="inbox icon" style={{ fontSize: 24, marginRight: 10, color: '#bbb' }} />
                            {emptyMessage || 'No Data Found'}
                        </span>
                    </div>
                )}
                {data.length > 0 && mobileCard && data.map((item, i) => (
                    <div
                        key={`${i}-app-card-${item.Id}`}
                        className="mb-3"
                    >
                        {cloneElement(mobileCard, {
                            item,
                            i,
                            draggingIndex,
                            onDragStart,
                            onDragOver,
                            onDragEnd,
                            onDrop,
                        })}
                    </div>
                ))}
                {data.length > 0 && !mobileCard && (
                    <DesktopTable
                        onDragStart={onDragStart}
                        onDragOver={onDragOver}
                        draggable={draggable}
                        onDragEnd={onDragEnd}
                        onDrop={onDrop}
                        draggingIndex={draggingIndex}
                        columns={columns}
                        data={data}
                        onRowClick={onRowClick}
                    />
                )}
            </MobileMode>
            {!hidePagination && (
                <div className="w-100 justify-content-end d-flex mt-2">
                    <PaginationComponent
                        activePage={filter?.PageNumber}
                        pageChanged={pageChanged}
                        pageSize={filter?.PageSize}
                        totalCount={totalCount}
                    />
                </div>
            )}
        </TableConatiner>
    )
}
